import { inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { extractApiError, extractHttpError } from '@shared/utils/error.utils';

/**
 * Valores admitidos en un query param.
 *
 * El arreglo no es un adorno: hay endpoints con query REPETIBLE
 * (`?anios=2025&anios=2026`). `HttpClient` repite la clave por elemento cuando
 * recibe un arreglo, que es justo lo que esperan. Con la firma anterior
 * —`Record<string, string>`— esa llamada no se podia ni escribir, y el generador
 * terminaba emitiendo el parametro como escalar.
 */
export type QueryParams = Record<
  string,
  string | number | boolean | ReadonlyArray<string | number | boolean>
>;

/**
 * Constraint para cualquier ResponseDto del API.
 * Cubre ambas variantes: con y sin `errors`.
 */
interface ApiResponseBase {
  errored?: boolean;
  errorMessage?: string | null;
  errors?: string[] | null;
  object?: unknown;
}

/**
 * Extrae el tipo de `object` de un ResponseDto.
 *
 * Ejemplo:
 *   ExtractObject<CatMainStagesIEnumerableResponseDto>
 *   → CatMainStages[] | null | undefined
 *
 *   NonNullable<ExtractObject<...>>
 *   → CatMainStages[]
 */
type ExtractObject<T extends ApiResponseBase> = T['object'];

/**
 * Servicio base para llamadas HTTP tipadas con manejo de errores.
 *
 * Usa los tipos DTO reales de los schemas como genérico. El tipo de
 * retorno se infiere automáticamente desde la propiedad `object` del DTO:
 *
 * ```typescript
 * export class ProspectService extends ApiBaseService {
 *   async getStages() {
 *     return this.get<CatMainStagesIEnumerableResponseDto>(url);
 *     // Retorna: CatMainStages[] | null | undefined
 *   }
 * }
 * ```
 *
 * Flujo:
 * 1. Convierte Observable → Promise (async/await)
 * 2. Verifica `errored === true` → lanza Error con mensaje del backend
 * 3. Retorna `response.object` con el tipo real del DTO
 * 4. Si la petición HTTP falla, extrae mensaje del body de error
 */
export abstract class ApiBaseService {
  protected http = inject(HttpClient);

  protected async get<TResponse extends ApiResponseBase>(
    url: string,
    params?: QueryParams,
  ): Promise<ExtractObject<TResponse>> {
    return this.request<TResponse>('GET', url, undefined, params);
  }

  protected async post<TResponse extends ApiResponseBase>(
    url: string,
    body: unknown,
    params?: QueryParams,
  ): Promise<ExtractObject<TResponse>> {
    return this.request<TResponse>('POST', url, body, params);
  }

  protected async put<TResponse extends ApiResponseBase>(
    url: string,
    body: unknown,
    params?: QueryParams,
  ): Promise<ExtractObject<TResponse>> {
    return this.request<TResponse>('PUT', url, body, params);
  }

  protected async patch<TResponse extends ApiResponseBase>(
    url: string,
    body: unknown,
    params?: QueryParams,
  ): Promise<ExtractObject<TResponse>> {
    return this.request<TResponse>('PATCH', url, body, params);
  }

  protected async delete<TResponse extends ApiResponseBase>(
    url: string,
    params?: QueryParams,
  ): Promise<ExtractObject<TResponse>> {
    return this.request<TResponse>('DELETE', url, undefined, params);
  }

  /**
   * Maneja respuestas que pueden venir envueltas en ResponseDto o no.
   * Útil cuando el schema no declara el wrapper pero el backend lo usa.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected async safeRequest<T>(obs: import('rxjs').Observable<any>): Promise<T> {
    try {
      const response: any = await lastValueFrom(obs);
      if (response && typeof response === 'object' && 'object' in response) {
        this.assertNoError(response);
        return response.object as T;
      }
      return response as T;
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        throw new Error(extractHttpError(error));
      }
      throw error;
    }
  }

  private async request<TResponse extends ApiResponseBase>(
    method: string,
    url: string,
    body?: unknown,
    params?: QueryParams,
  ): Promise<ExtractObject<TResponse>> {
    try {
      const response = await lastValueFrom(
        this.http.request<TResponse>(method, url, { body, params }),
      );
      this.assertNoError(response);
      return response.object as ExtractObject<TResponse>;
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        throw new Error(extractHttpError(error));
      }
      throw error;
    }
  }

  private assertNoError(response: ApiResponseBase): void {
    const error = extractApiError(response);
    if (error) throw new Error(error);
  }
}
